'use client'

import { useEffect, useState, useRef, useCallback } from 'react'
import { usePathname, useSearchParams } from 'next/navigation'

export const LoadingBar = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [progress, setProgress] = useState(0)
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const progressInterval = useRef<ReturnType<typeof setInterval> | null>(null)
  const completionTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)
  const startTime = useRef<number | null>(null)
  const previousPath = useRef<string>('')
  const previousSearch = useRef<string>('')
  const isInitialLoad = useRef(true)
  const isMounted = useRef(true)

  // Cleanup function to prevent memory leaks
  const cleanupTimers = useCallback(() => {
    if (progressInterval.current) {
      clearInterval(progressInterval.current)
      progressInterval.current = null
    }
    if (completionTimeout.current) {
      clearTimeout(completionTimeout.current)
      completionTimeout.current = null
    }
  }, [])

  // Complete loading with cleanup
  const completeLoading = useCallback(() => {
    if (!isMounted.current) return
    
    cleanupTimers()
    setProgress(100)
    
    // Clear any existing completion timeout before setting a new one
    if (completionTimeout.current) {
      clearTimeout(completionTimeout.current)
    }
    
    completionTimeout.current = setTimeout(() => {
      if (!isMounted.current) return
      setIsLoading(false)
      setProgress(0)
      startTime.current = null
    }, 200)
  }, [cleanupTimers])

  // Monitor real page loading state (for full page reloads only)
  useEffect(() => {
    if (!isLoading) return

    const handleLoad = () => {
      if (isLoading) {
        completeLoading()
      }
    }

    const handleDOMContentLoaded = () => {
      if (isLoading) {
        setProgress(75)
      }
    }

    // Check if DOMContentLoaded already fired
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', handleDOMContentLoaded)
    } else if (document.readyState === 'interactive' || document.readyState === 'complete') {
      // Already loaded, set progress immediately (deferred to avoid cascading renders)
      setTimeout(() => {
        setProgress(75)
      }, 0)
    }

    // Check if load already fired
    if (document.readyState === 'complete') {
      // Defer to avoid cascading renders
      setTimeout(() => {
        completeLoading()
      }, 0)
    } else {
      window.addEventListener('load', handleLoad)
    }

    return () => {
      window.removeEventListener('load', handleLoad)
      document.removeEventListener('DOMContentLoaded', handleDOMContentLoaded)
    }
  }, [isLoading, completeLoading])

  // Navigation detection
  useEffect(() => {
    const currentPath = pathname || ''
    const currentSearch = searchParams?.toString() || ''

    // Handle initial load
    if (isInitialLoad.current) {
      isInitialLoad.current = false
      previousPath.current = currentPath
      previousSearch.current = currentSearch
      return
    }

    // Only trigger loading if we're actually navigating to a different path
    const hasPathChanged = currentPath !== previousPath.current
    const hasSearchChanged = currentSearch !== previousSearch.current

    if (hasPathChanged || hasSearchChanged) {
      // Clean up any existing timers
      cleanupTimers()

      // Start loading (deferred to avoid cascading renders)
      setTimeout(() => {
        setIsLoading(true)
        setProgress(0)
        startTime.current = Date.now()

        // Start progress animation based on time
        progressInterval.current = setInterval(() => {
          if (!isMounted.current || !startTime.current) return

          setProgress(prev => {
            const elapsed = Date.now() - startTime.current!
            const expectedDuration = 1200 // 1.2 seconds expected
            const calculatedProgress = Math.min(
              (elapsed / expectedDuration) * 80,
              80
            )

            // Use the higher of calculated or current progress, but don't go backwards
            return Math.max(prev, Math.min(prev + 0.3, calculatedProgress))
          })
        }, 50) // Reduced from 30ms to 50ms (20fps is sufficient for progress bar)

        // Auto-complete after reasonable time (fallback)
        completionTimeout.current = setTimeout(() => {
          completeLoading()
        }, 3000) // 3 second fallback
      }, 0)
    }

    // Update previous values
    previousPath.current = currentPath
    previousSearch.current = currentSearch
  }, [pathname, searchParams, cleanupTimers, completeLoading])

  // Cleanup on unmount
  useEffect(() => {
    isMounted.current = true
    return () => {
      isMounted.current = false
      cleanupTimers()
    }
  }, [cleanupTimers])

  if (!isLoading) return null

  return (
    <div
      className="fixed top-0 left-0 w-full z-[9999] bg-gray-900/20 border-b border-gray-900/10"
      style={{ height: '3px' }}
      role="progressbar"
      aria-label="Page loading progress"
      aria-valuenow={progress}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div
        className="h-full bg-gradient-to-r from-vi-red via-red-500 to-vi-red transition-all duration-200 ease-out shadow-[0_0_8px_rgba(192,64,64,0.9),0_1px_2px_rgba(0,0,0,0.1)]"
        style={{
          width: `${progress}%`,
        }}
      />
    </div>
  )
}
